import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  InputAdornment,
} from '@mui/material';
import { createVoucher, fetchAffiliates, fetchPartnerVouchers } from '../services/api';

const VoucherForm = () => {
  const [voucherCode, setVoucherCode] = useState('');
  const [shortUrl, setShortUrl] = useState('');
  const [customPath, setCustomPath] = useState('');
  const [creating, setCreating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [affiliates, setAffiliates] = useState([]);
  const [selectedAffiliateId, setSelectedAffiliateId] = useState('');
  const [loadingAffiliates, setLoadingAffiliates] = useState(true);
  
  // Add state for vouchers
  const [vouchers, setVouchers] = useState([]);
  const [loadingVouchers, setLoadingVouchers] = useState(false);

  // Fetch affiliates when component mounts
  useEffect(() => {
    const getAffiliates = async () => {
      setLoadingAffiliates(true);
      try {
        const data = await fetchAffiliates();
        setAffiliates(data);
        // Set the first affiliate as default if available
        if (data.length > 0) {
          setSelectedAffiliateId(data[0].id.toString());
        }
      } catch (error) {
        console.error('Error fetching affiliates:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load affiliates. Please try again.',
          severity: 'error'
        });
      } finally {
        setLoadingAffiliates(false);
      }
    };

    getAffiliates();
  }, []);

  // Fetch vouchers when affiliate is selected
  useEffect(() => {
    if (selectedAffiliateId) {
      const getVouchers = async () => {
        setLoadingVouchers(true);
        try {
          const data = await fetchPartnerVouchers(selectedAffiliateId);
          setVouchers(data || []);
        } catch (error) {
          console.error('Error fetching vouchers:', error);
          setSnackbar({
            open: true,
            message: 'Failed to load vouchers. Please try again.',
            severity: 'error'
          });
          setVouchers([]);
        } finally {
          setLoadingVouchers(false);
        }
      };

      getVouchers();
    } else {
      setVouchers([]);
    }
  }, [selectedAffiliateId]);

  const handleCreateVoucher = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!voucherCode.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a voucher code',
        severity: 'error'
      });
      return;
    }

    if (!selectedAffiliateId) {
      setSnackbar({
        open: true,
        message: 'Please select an affiliate',
        severity: 'error'
      });
      return;
    }
    
    setCreating(true);
    try {
      // Call API to create voucher with selected affiliate ID
      await createVoucher(selectedAffiliateId, voucherCode, customPath.trim());
      
      // // Create short URL if customPath is provided
      // if (customPath.trim()) {
      //   const shortUrlResponse = await createShortUrl(voucherCode, customPath.trim());
      //   setShortUrl(shortUrlResponse.url);
      // }
      
      // Success
      setSnackbar({
        open: true,
        message: 'Voucher created successfully!',
        severity: 'success'
      });
      
      // Reset form
      setVoucherCode('');
      setCustomPath('');
      
      // Refresh vouchers list
      const data = await fetchPartnerVouchers(selectedAffiliateId);
      setVouchers(data || []);
    } catch (error) {
      console.error('Error creating voucher:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to create voucher. Please try again.',
        severity: 'error'
      });
      setShortUrl('');
    } finally {
      setCreating(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Create New Voucher
      </Typography>
      
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Box component="form" onSubmit={handleCreateVoucher} noValidate>
          <FormControl fullWidth margin="normal" required disabled={loadingAffiliates || creating}>
            <InputLabel id="affiliate-select-label">Select Affiliate</InputLabel>
            <Select
              labelId="affiliate-select-label"
              id="affiliate-select"
              value={selectedAffiliateId}
              label="Select Affiliate"
              onChange={(e) => setSelectedAffiliateId(e.target.value)}
            >
              {loadingAffiliates ? (
                <MenuItem disabled>Loading affiliates...</MenuItem>
              ) : affiliates.length > 0 ? (
                affiliates.map((affiliate) => (
                  <MenuItem key={affiliate.id} value={affiliate.id.toString()}>
                    {affiliate.name} ({affiliate.partner_name})
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No affiliates available</MenuItem>
              )}
            </Select>
          </FormControl>
          
          <Box sx={{ mb: 3, mt: 2 }}>
            <TextField
              label="Voucher Code"
              fullWidth
              value={voucherCode}
              onChange={(e) => setVoucherCode(e.target.value)}
              placeholder="Enter voucher code"
              variant="outlined"
              required
              disabled={creating}
              inputProps={{
                'aria-label': 'voucher code',
              }}
            />
            <Typography variant="caption" color="textSecondary">
              Voucher code must be unique and can contain letters, numbers, and underscores
            </Typography>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle1" gutterBottom>
            Short URL
          </Typography>
          
          <Box sx={{ mb: 3 }}>
          <TextField
            label="Custom Path"
            fullWidth
            value={customPath}
            onChange={(e) => setCustomPath(e.target.value)}
            placeholder="e.g., promo-2023"
            variant="outlined"
            disabled={creating}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  https://sjk.app/
                </InputAdornment>
              ),
            }}
            inputProps={{
              'aria-label': 'custom path',
            }}
            sx={{ mb: 1 }}
          />
          <Typography variant="caption" color="textSecondary">
            Custom path for your short URL
          </Typography>
          </Box>
          
          {shortUrl && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
              <Typography variant="body2">
                Short URL created: <strong>{shortUrl}</strong>
              </Typography>
            </Box>
          )}
          
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={creating || loadingAffiliates || !selectedAffiliateId}
            startIcon={creating ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ mt: 2 }}
          >
            {creating ? 'Creating...' : 'Create Voucher'}
          </Button>
        </Box>
      </Paper>
      
      {/* Vouchers Table */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Vouchers for Selected Affiliate
        </Typography>
        
        {loadingVouchers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : vouchers.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Voucher Code</TableCell>
                  <TableCell>Short URL</TableCell>
                  <TableCell>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vouchers.map((voucher) => (
                  <TableRow key={voucher.voucher_code}>
                    <TableCell component="th" scope="row">
                      {voucher.voucher_code}
                    </TableCell>
                    <TableCell>
                      {voucher.short_url}
                    </TableCell>
                    <TableCell>
                      {new Date(voucher.created_at).toLocaleDateString('id-ID', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : selectedAffiliateId ? (
          <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
            No vouchers found for this affiliate.
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
            Please select an affiliate to view vouchers.
          </Typography>
        )}
      </Paper>
      
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VoucherForm;